import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
import SocioVueComponents, {
  initializeTracingAndLogging,
  setTelemetryUser,
} from "@socotec.io/socio-vue-components";
import { getDefaultSentryConfig } from "@socotec.io/socio-grpc-api/configs";
import { createOidc } from "vue3-oidc";
import "dayjs/locale/fr";
import "@/scss/variables.scss";

import App from "./App.vue";
import PackageJson from "../package.json";
import SocioManifest from "../socio-manifest.json";
import router from "./router";
import { preloadRouteComponents } from "@/utils/router";
import pinia from "./store";
import i18n from "./setup/i18n";
import createAtmoVuetify from "./setup/vuetify";
import { oidcSettings } from "./setup/OIDCSettings";
import {
  ensureLiveSession,
  handleAccessTokenExpired,
  handleSilentRenewError,
  reconnect,
  sessionExpired,
  syncOidcUser,
  watchSessionRecovery,
} from "./setup/oidcSessionRecovery";
import workbox from "./registerServiceWorker";
import db from "./db";
import {
  initAppRefreshListener,
  isDeletedDatabaseError,
} from "./composables/useAppRefresh";
import { installVueErrorHandler } from "./setup/vueErrorHandler";
import { openDatabaseOrShowError } from "./setup/databaseOpenGuard";
import { attachAxiosResponse } from "./setup/sentryAxiosResponse";
import { makeSentryTransport } from "./setup/sentryTransport";
import { addNetworkContext } from "./setup/sentryNetworkContext";
import { classifyExpectedFailure } from "./setup/sentryExpectedFailure";
import { isDeviceOfflineFailure } from "./utils/network";
import { useProjectsStore } from "./store/modules/projects";
import {
  flagStaleBuildReload,
  reloadAfterStaleBuild,
  reportStaleBuild,
} from "./utils/staleBuild";
import number from "./directives/number";

const faroSettings = {
  enableTelemetry: import.meta.env.VITE_APP_ENABLE_TELEMETRY,
  grafanaApiKey: import.meta.env.VITE_APP_GRAFANA_API_KEY,
  collectorUrl: import.meta.env.VITE_APP_COLLECTOR_URL,
  propagateUrls: import.meta.env.VITE_APP_PROPAGATE_URLS,
  grpcApiEnv: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  faroLogLevel: import.meta.env.VITE_APP_FARO_LOG_LEVEL,
  appVersion: import.meta.env.VITE_APP_VERSION,
};

initializeTracingAndLogging(SocioManifest, PackageJson, faroSettings, {
  router,
});

await createOidc({
  oidcSettings,
  auth: false,
  events: {
    addUserLoaded: (user) => {
      syncOidcUser(user);
      setTelemetryUser(user);
    },
    addUserUnloaded: () => syncOidcUser(null),
    addSilentRenewError: handleSilentRenewError,
    addAccessTokenExpired: handleAccessTokenExpired,
  },
});

const isSigninCallback =
  globalThis.location.pathname === new URL(oidcSettings.redirect_uri).pathname;

if (!isSigninCallback) {
  await ensureLiveSession();
  // Au démarrage, aucune saisie à perdre : on renvoie directement vers Keycloak.
  if (sessionExpired.value) await reconnect();
}

watchSessionRecovery();

initAppRefreshListener();

const app = createApp(App);

const SLOT_HOST_PATTERN = /at <([^\s>]+)/;
const slotHostComponent = (trace) =>
  SLOT_HOST_PATTERN.exec(String(trace))?.[1] ?? "";

const LIBRARY_SLOT_HOSTS = new Set([
  "BaseTransition",
  "Transition",
  "MaybeTransition",
  "ExpandTransition",
  "ExpandXTransition",
]);

const isLibrarySlotHost = (host) =>
  /^V[A-Z]/.test(host) || LIBRARY_SLOT_HOSTS.has(host);

const isVuetifyInternalSlotWarning = (msg, trace) =>
  /^Slot "[^"]+" invoked outside of the render function/.test(msg) &&
  isLibrarySlotHost(slotHostComponent(trace));

const duplicateI18nCallerFrame = (stack) => {
  const frames = String(stack).split("\n");
  const intlifyFrame = frames.findIndex((frame) =>
    frame.trimStart().startsWith("at useI18n")
  );
  return intlifyFrame === -1 ? "" : (frames[intlifyFrame + 1] ?? "");
};

if (import.meta.env.DEV) {
  const forwardWarn = console.warn;
  console.warn = (...args) => {
    const [message] = args;
    if (
      typeof message === "string" &&
      message.includes("Duplicate `useI18n` calling by local scope")
    ) {
      const caller = duplicateI18nCallerFrame(
        new Error("i18n warn trace").stack
      );
      if (caller && !caller.includes("/src/")) return;
    }
    forwardWarn(...args);
  };
}

const defaultWarnHandler = app.config.warnHandler;
app.config.warnHandler = (msg, instance, trace) => {
  if (isVuetifyInternalSlotWarning(msg, trace)) return;
  if (defaultWarnHandler) {
    defaultWarnHandler(msg, instance, trace);
    return;
  }
  console.warn(`[Vue warn]: ${msg}${trace}`);
};

Sentry.init({
  ...getDefaultSentryConfig({
    app,
    router,
    dsn: "https://458b6e76e52d1cd40d0a90e5804cc780@sentry.tools.infra-socotec.net/15",
    environment: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
    release: `atmo@${import.meta.env.VITE_APP_VERSION}`,
    enabled: import.meta.env.MODE !== "development",
  }),
  transport: makeSentryTransport,
  beforeSend: (event, hint) =>
    isDeletedDatabaseError(hint?.originalException) ||
    isDeviceOfflineFailure(hint?.originalException)
      ? null
      : addNetworkContext(
          classifyExpectedFailure(attachAxiosResponse(event, hint), hint)
        ),
});

const isDatabaseOpen = await openDatabaseOrShowError();

installVueErrorHandler(app, router);
flagStaleBuildReload();

globalThis.addEventListener("vite:preloadError", async (event) => {
  reportStaleBuild({
    reason: "preload",
    routeName: String(router.currentRoute.value.name ?? "unknown"),
    error: event.payload,
  });

  if (
    !navigator.onLine ||
    (isDatabaseOpen && useProjectsStore().isOfflineState)
  )
    return;

  await reloadAfterStaleBuild(globalThis.location.href);
});

app.config.globalProperties.$workbox = workbox;
app.config.globalProperties.$db = db;
app.config.globalProperties.$isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

app.directive("number", number);

if (isDatabaseOpen) {
  app.use(pinia);
  app.use(router);
  app.use(i18n);
  app.use(createAtmoVuetify(i18n));
  app.use(SocioVueComponents, i18n);

  app.mount("#app");

  if (import.meta.env.DEV) preloadRouteComponents(router);
}
